import { Badge, Group, Stack, TagsInput, Text } from '@mantine/core';
import { useEffect, useMemo, useState, type FC } from 'react';

export type VendorOverrides = Record<string, { to?: string[]; cc?: string[] }>;

/**
 * A per-counterparty address book: a pill per vendor, and a To and CC for whichever is picked.
 *
 * LIFTED OUT OF THE ORDER EMAIL SECTION rather than written a second time. The receipt QC card
 * needed the same block - the brand's QC head for one site is not its QC head for another - and
 * two copies of a picker that both call "set" the same thing is the kind of pair that drifts
 * the first time one of them is fixed. The words that differ (who the party is, what a blank
 * To falls back to) come in as props; the mechanics do not.
 *
 * STATE IS THE PARENT'S. This only knows which vendor is selected; the book itself is passed in
 * and handed back on every change, so the parent's Save sends exactly what is on screen.
 */
const VendorRecipientOverrides: FC<{
    vendors: Array<{ vendorId: string; name: string }> | undefined;
    byVendor: VendorOverrides;
    onChange: (next: VendorOverrides) => void;
    editable: boolean;
    /** "manufacturer" or "supplier" - the noun in the prompt above the pills. */
    party: string;
    /** What the pills are, in a sentence, under the prompt. */
    hint: string;
    /** What happens when a vendor's To is left blank. */
    toDescription: string;
    ccDescription: string;
}> = ({ vendors, byVendor, onChange, editable, party, hint, toDescription, ccDescription }) => {
    const [selected, setSelected] = useState<string | null>(null);

    useEffect(() => {
        if (!selected && vendors?.length) setSelected(vendors[0].vendorId);
    }, [vendors, selected]);

    const configured = useMemo(
        () =>
            new Set(
                Object.entries(byVendor)
                    .filter(([, v]) => (v.to?.length ?? 0) > 0 || (v.cc?.length ?? 0) > 0)
                    .map(([id]) => id),
            ),
        [byVendor],
    );

    const setField = (field: 'to' | 'cc', value: string[]) => {
        if (!selected) return;
        onChange({ ...byVendor, [selected]: { ...byVendor[selected], [field]: value } });
    };

    const current = selected ? (byVendor[selected] ?? {}) : {};

    return (
        <Stack gap="sm">
            <div>
                <Text size="sm" fw={500} mb={4}>
                    {`Select a ${party} to configure its emails`}
                </Text>
                <Text size="xs" c="dimmed" mb="xs">
                    {hint}
                </Text>
                <Group gap="xs" wrap="wrap">
                    {vendors?.map((vendor) => (
                        <Badge
                            key={vendor.vendorId}
                            size="lg"
                            variant={selected === vendor.vendorId ? 'filled' : 'outline'}
                            color={configured.has(vendor.vendorId) ? 'blue' : 'gray'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelected(vendor.vendorId)}
                        >
                            {vendor.name || vendor.vendorId}
                            {configured.has(vendor.vendorId) && ' · set'}
                        </Badge>
                    ))}
                </Group>
            </div>

            {selected && (
                <Stack gap="sm">
                    <TagsInput
                        label="To"
                        description={toDescription}
                        placeholder="name@company.com"
                        value={current.to ?? []}
                        disabled={!editable}
                        onChange={(v) => setField('to', v)}
                    />
                    <TagsInput
                        label="CC"
                        description={ccDescription}
                        placeholder="name@company.com"
                        value={current.cc ?? []}
                        disabled={!editable}
                        onChange={(v) => setField('cc', v)}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default VendorRecipientOverrides;
